import { useUpdateSlideMutation } from "@api/slides-library";
import Button from "@components/ui/button";
import { cn } from "@lib/cn";
import { FoldersSelect } from "@modules/slides-library/features/folders";
import { DialogBase } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Spinner from "@/components/ui/spinner";
import { handleAsyncOperation } from "@/shared";
import { useGetSlidesFoldersQuery } from "@/shared/services";
import type { TSavedSlide, TSlidesFolder } from "@/shared/types";
import { SPECIAL_FOLDERS } from "@modules/slides-library/features/folders/constants";

const DIALOG_ID = "move-slide-dialog";

const SlideMoveDialog = ({ slide }: { slide: TSavedSlide }) => {
  "use no memo";
  const dispatch = useAppDispatch();
  const { data: folders } = useGetSlidesFoldersQuery();
  const [updateSlide, { isLoading }] = useUpdateSlideMutation();
  const [folder, setFolder] = useState<{ title: string; id: string }>({
    title: "",
    id: "",
  });
  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;
    handleAsyncOperation({
      operation: async () => {
        await updateSlide({
          id: slide._id,
          folderId: folder.id,
          title: slide.title,
        }).unwrap();
        toast.success(`Slide moved to ${folder.title}`);
        handleClose();
      },
      onError: () => toast.error("Failed to move slide"),
    });
  }, [
    updateSlide,
    handleClose,
    isLoading,
    slide._id,
    slide.title,
    folder.id,
    folder.title,
  ]);

  useEffect(() => {
    if (!folders) return;
    const slideFolderId = slide.folderId ?? "";

    if (SPECIAL_FOLDERS[slideFolderId]) {
      setFolder({ title: SPECIAL_FOLDERS[slideFolderId], id: slideFolderId });
      return;
    }

    const folder = folders.find(
      (folder: TSlidesFolder) => folder._id === slideFolderId,
    );
    if (!folder) return;
    setFolder({ title: folder.title, id: folder._id });
  }, [folders, slide.folderId]);

  return (
    <DialogBase>
      <DialogBase.Header
        title={`Move “${slide.title}”`}
        onClose={handleClose}
      />
      <DialogBase.Content>
        <FoldersSelect folder={folder} setFolder={setFolder} />
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!folder.id}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

SlideMoveDialog.id = DIALOG_ID;
export { SlideMoveDialog };
